import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import Button from 'domains/core/components/Button';
import { Stack, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';

type Props = {
    activeStep: CustomCampaignSteps;
    isContinueEnabled: boolean;
    onContinueClick: () => void;
    onPublishClick: () => void;
    onPublishAndResumeClick: () => void;
    onSaveDraftClick: () => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
};

// Reusable Save as Draft button component
const SaveAsDraftButton = ({ onClick }: { onClick: () => void }) => (
    <Button
        onClick={onClick}
        variant="outlined"
        sx={{ width: '146px' }}
        data-testid="save-as-draft-button"
        role="button"
    >
        Save as Draft
    </Button>
);

/**
 *
 * @param onClick - The function to call when the button is clicked.
 * @returns A button that when clicked, will call the onClick function which will be resume functionality.
 */
const ResumeButton = ({ onClick }: { onClick: () => void }) => (
    <Tooltip title="Publish changes and resume curation" arrow>
        <Button onClick={onClick} variant="contained" data-testid="resume-button" role="button">
            Resume
        </Button>
    </Tooltip>
);

const CustomCampaignNavigationButtons = ({
    activeStep,
    isContinueEnabled,
    onContinueClick,
    onPublishClick,
    onPublishAndResumeClick,
    onSaveDraftClick,
    setIsContinueEnabled,
}: Props) => {
    const { curationsDraft: hasCurationsDraft, editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const { canPublishCurations, canManageCurationsDrafts } = useAccessCentrePermissions();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    useEffect(() => {
        // Disabling the continue button with every step change
        // as every step has its own unique validation requirements
        setIsContinueEnabled(false);
    }, [activeStep, setIsContinueEnabled]);

    const hideNavigationButtons =
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP ||
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT ||
        activeStep === CustomCampaignSteps.SELECT_PATH_ACTION;
    if (hideNavigationButtons) return;

    if (activeStep === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
        // For paused curations: Save on left, Resume on right
        if (hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED) {
            return (
                <Stack direction="row" spacing={2}>
                    {canPublishCurations && (
                        <Button onClick={onPublishClick} variant="outlined">
                            Save
                        </Button>
                    )}
                    {canPublishCurations && <ResumeButton onClick={onPublishAndResumeClick} />}
                </Stack>
            );
        }

        // For other curations: Save as Draft (optional), Publish
        return (
            <Stack direction="row" spacing={2}>
                {hasCurationsDraft && canManageCurationsDrafts && <SaveAsDraftButton onClick={onSaveDraftClick} />}
                {canPublishCurations && (
                    <Button onClick={onPublishClick} variant="outlined">
                        Publish
                    </Button>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="row" spacing={2}>
            {hasCurationsDraft &&
                canManageCurationsDrafts &&
                status !== CampaignStatus.PAUSED &&
                activeStep !== CustomCampaignSteps.NAME_CAMPAIGN && <SaveAsDraftButton onClick={onSaveDraftClick} />}
            <Button disabled={!isContinueEnabled} onClick={onContinueClick} variant="outlined">
                Continue
            </Button>
        </Stack>
    );
};

export default CustomCampaignNavigationButtons;
